import { useEffect, useId, useRef } from "react";
import type { ReactNode, RefObject } from "react";

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface StaffDialogProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Where focus goes when the dialog closes. Defaults to whatever had focus
   *  when it opened; pass the opener when that element unmounts on open. */
  returnFocusRef?: RefObject<HTMLElement | null>;
}

/**
 * A hand-written modal: focus moves in on open, Tab stays inside, Escape
 * closes, and focus returns on close. Never window.confirm — blocking dialogs
 * misbehave in the third-party iframe and break the embed tooling.
 */
export function StaffDialog({ title, onClose, children, returnFocusRef }: StaffDialogProps): React.ReactElement {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const returnTo = returnFocusRef?.current ?? (document.activeElement as HTMLElement | null);
    const dialog = dialogRef.current;
    (dialog?.querySelector<HTMLElement>(FOCUSABLE) ?? dialog)?.focus();
    return () => returnTo?.focus();
  }, [returnFocusRef]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== "Tab" || !dialogRef.current) return;
    const items = [...dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl"
      >
        <h2 id={titleId} className="text-lg font-semibold text-gray-900">
          {title}
        </h2>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}
